import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Glass & Glazing",
  "Property Maintenance",
  "Garden Maintenance",
  "A bit of everything",
  "Not sure yet",
];

const EMPTY = { name: "", phone: "", suburb: "", service: SERVICES[0], details: "" };

export const QuoteModal = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      const t = setTimeout(() => firstRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
    document.body.style.overflow = "";
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Quote request sent. We'll be in touch shortly.");
      setForm(EMPTY);
      onClose();
    } catch {
      toast.error("Couldn't send that. Call us on 0424 424 600 instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quote-modal"
          className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quote-modal-title"
        >
          <motion.button
            type="button"
            aria-label="Close quote form"
            data-testid="quote-modal-backdrop"
            className="absolute inset-0 cursor-default bg-navy/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 48 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto border-t-8 border-orange bg-white"
          >
            <div className="flex items-start justify-between bg-navy px-7 py-6 text-white">
              <div>
                <h2 id="quote-modal-title" className="font-display text-2xl font-bold uppercase tracking-wide">
                  Get a quote
                </h2>
                <p className="mt-1 text-sm text-white/60">
                  Tell us what needs fixing. We&rsquo;ll come back with a straight answer.
                </p>
              </div>
              <button
                type="button"
                data-testid="quote-modal-close-button"
                aria-label="Close"
                onClick={onClose}
                className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-white/25 transition-colors duration-200 hover:border-orange"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-5 px-7 py-7">
              <div>
                <label htmlFor="quote-name" className="field-label">
                  Name
                </label>
                <input
                  id="quote-name"
                  ref={firstRef}
                  data-testid="quote-name-input"
                  className="field-input"
                  value={form.name}
                  onChange={set("name")}
                  required
                  minLength={2}
                  maxLength={80}
                  autoComplete="name"
                  placeholder="Your name"
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="quote-phone" className="field-label">
                    Phone
                  </label>
                  <input
                    id="quote-phone"
                    data-testid="quote-phone-input"
                    className="field-input"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    required
                    minLength={6}
                    maxLength={20}
                    autoComplete="tel"
                    placeholder="04xx xxx xxx"
                  />
                </div>
                <div>
                  <label htmlFor="quote-suburb" className="field-label">
                    Suburb
                  </label>
                  <input
                    id="quote-suburb"
                    data-testid="quote-suburb-input"
                    className="field-input"
                    value={form.suburb}
                    onChange={set("suburb")}
                    required
                    minLength={2}
                    maxLength={80}
                    autoComplete="address-level2"
                    placeholder="e.g. Morphett Vale"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="quote-service" className="field-label">
                  What needs doing?
                </label>
                <select
                  id="quote-service"
                  data-testid="quote-service-select"
                  className="field-input"
                  value={form.service}
                  onChange={set("service")}
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="quote-details" className="field-label">
                  Job details
                </label>
                <textarea
                  id="quote-details"
                  data-testid="quote-details-input"
                  className="field-input min-h-28 resize-y"
                  value={form.details}
                  onChange={set("details")}
                  required
                  minLength={5}
                  maxLength={2000}
                  placeholder="e.g. Cracked window pane in the front bedroom, roughly 60 x 90 cm."
                />
              </div>
              <button
                type="submit"
                data-testid="quote-submit-button"
                disabled={sending}
                className="btn-primary w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? "Sending..." : "Send quote request"}
              </button>
              <p className="text-center text-xs text-navy/50">
                Prefer to talk? Call{" "}
                <a href="tel:0424424600" className="font-semibold text-orange">
                  0424 424 600
                </a>
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
